import React, { useState, useEffect } from "react";
import LogoIcon from "../../assets/images/login_form_logo.png";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import bgImage from "../../assets/images/bg-img.jpg";
import { Eye, EyeOff } from "lucide-react";
import { MdEmail, MdPhone } from "react-icons/md";
import { IoMdPerson } from "react-icons/io";
import { FaLongArrowAltRight } from "react-icons/fa";

function SignUp() {
  const [userType, setUserType] = useState("");
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    document.getElementById("header-container").style.display = "none";
  }, []);

  // Handle form field changes

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    let tempErrors = {};

    // Ensure formData exists
    if (!formData) {
      console.error("formData is undefined or null");
      return false;
    }

    // Required Fields Validation
    if (!formData.first_name?.trim())
      tempErrors.first_name = "First name is required";
    if (!formData.last_name?.trim())
      tempErrors.last_name = "Last name is required";
    if (!formData.email?.trim()) tempErrors.email = "Email is required";
    if (!formData.password?.trim())
      tempErrors.password = "Password is required";
    if (!formData.phone?.trim()) tempErrors.phone = "Phone number is required";
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      tempErrors.email = "Invalid email format";
    }

    if (formData.password && formData.password.length < 6) {
      tempErrors.password = "Password must be at least 6 characters";
    }

    if (formData.phone && !/^\d{10}$/.test(formData.phone)) {
      tempErrors.phone = "Phone number must be 10 digits";
    }

    setErrors(tempErrors);

    return Object.keys(tempErrors).length === 0; // Returns true if no errors
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    let filteredData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      password: formData.password,
      phone: formData.phone,
      user_type: userType,
    };

    console.log("Sending data:", filteredData);

    try {
      let endpoint = "";
      if (
        userType === "Candidate" ||
        userType === "HR" ||
        userType === "Team Lead"
      ) {
        endpoint = `${process.env.REACT_APP_API_KEY}/add_user`;
      }

      const response = await axios.post(endpoint, filteredData, {
        headers: { "Content-Type": "application/json" },
      });

      console.log("Response:", response.data);
      toast.success("Registration Successful!");
      navigate("/login");
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message);

      // Check for duplicate email error
      if (error.response?.status === 409) {
        toast.error(
          "This email is already registered. Try logging in instead!"
        );
      } else {
        toast.error(error.response?.data?.error || "Registration Failed!");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex justify-center items-center min-h-[calc(100vh-20px)] px-4 sm:px-6 lg:px-8"
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 w-full max-w-6xl shadow-xl rounded-2xl bg-white overflow-hidden">
        {/* Left Side - Image Section */}
        <div className="flex justify-center items-center p-6 bg-gray-100">
          <img
            src={LogoIcon}
            className="w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-xs"
            alt="Registration Illustration"
          />
        </div>

        {/* Right Side - Registration Form */}
        <div className="p-6 sm:p-8 w-full">
          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-700">
              Register to Get Started
            </h2>
            <p className="text-gray-500 text-sm sm:text-base mt-1">
              Select your role to continue
            </p>
          </div>

          {/* User Type Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Select User Type :
            </label>
            <select
              className="w-full px-4 py-2 mt-1 border rounded-lg text-sm sm:text-base"
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
            >
              <option value="">-- Select --</option>
              <option value="Candidate">Candidate</option>
              <option value="HR">HR</option>
              <option value="Team Lead">Team Lead</option>
              <option value="Rishbh Shah" className="hidden">
                Owner
              </option>
            </select>
          </div>

          {userType && (
            <form onSubmit={handleSubmit}>
              {/* First Name */}
              <div className="relative mb-4">
                <input
                  type="text"
                  placeholder="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg"
                />
                {errors.first_name && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.first_name}
                  </p>
                )}
                <IoMdPerson
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  size={20}
                />
              </div>
              {/* Last Name */}
              <div className="relative mb-4">
                <input
                  type="text"
                  placeholder="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg"
                />
                {errors.last_name && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.last_name}
                  </p>
                )}
                <IoMdPerson
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  size={20}
                />
              </div>
              {/* Email */}
              <div className="relative mb-4">
                <input
                  type="email"
                  placeholder="Email Address"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg"
                />{" "}
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
                <MdEmail
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  size={20}
                />
              </div>
              {/* Password */}
              <div className="relative mb-4">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg"
                />
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
                <button
                  type="button"
                  className="absolute inset-y-0 right-3 flex items-center text-gray-600"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {/* Phone */}
              <div className="relative mb-4">
                <input
                  type="text"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg"
                />
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                )}
                <MdPhone
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  size={20}
                />
              </div>

              {/* Signup Button */}
              <button
                type="submit"
                className="w-full mt-4 bg-blue-700 hover:bg-blue-800 text-white py-2 sm:py-3 rounded-lg shadow-lg transition text-sm sm:text-base font-semibold  disabled:bg-gray-500 disabled:cursor-not-allowed "
                disabled={loading}
              >
                SIGN UP
              </button>

              <p className="text-center text-sm sm:text-base text-gray-600 mt-4">
                Already Registered?{" "}
                <span
                  onClick={() => navigate("/login")}
                  className="text-indigo-700 font-semibold cursor-pointer hover:underline"
                >
                  Login
                  <FaLongArrowAltRight className="inline cursor-pointer text-blue-700 hover:underline text-blue-800" />
                </span>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default SignUp;
